import {
    createContext,
    type PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useState,
    useSyncExternalStore,
} from 'react';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { useAuth } from '@/providers/auth';
import { parseSegmentCookie, toShopperSegment } from '@/lib/segmentation/cookie';
import { isAllowedBySegment, NO_GATING_SEGMENT, type ShopperSegment } from '@/lib/segmentation/product-eligibility';

// Same two-source model as BasketProvider: the loader value seeds SSR, the cookie wins after hydration.
// Cookie changes are not observable, so consumers call the refresh function after a segment-changing response.
// eslint-disable-next-line @typescript-eslint/no-empty-function
const subscribeNoop = () => () => {};
const readDocumentCookie = () => (typeof document === 'undefined' ? '' : document.cookie);
const getServerCookieSnapshot = () => '';

// eslint-disable-next-line react-refresh/only-export-components
export const SegmentContext = createContext<ShopperSegment>(NO_GATING_SEGMENT);
// eslint-disable-next-line react-refresh/only-export-components, @typescript-eslint/no-empty-function
export const SegmentRefreshContext = createContext<() => void>(() => {});

/** Seed segment from the loader; prefer a matching cookie after hydration. */
export function SegmentationProvider({ value, children }: PropsWithChildren<{ value: ShopperSegment }>) {
    const auth = useAuth();
    const { site } = useSite();
    // Re-rendering via refresh() makes useSyncExternalStore re-read document.cookie.
    const [, setVersion] = useState(0);
    const cookieHeader = useSyncExternalStore(subscribeNoop, readDocumentCookie, getServerCookieSnapshot);

    const segment = useMemo(() => {
        const payload = cookieHeader ? parseSegmentCookie(cookieHeader) : null;
        if (payload && payload.customerId === auth?.customerId && payload.siteId === site?.id) {
            return toShopperSegment(payload);
        }
        return value;
    }, [cookieHeader, value, auth?.customerId, site?.id]);

    const refresh = useCallback(() => setVersion((current) => current + 1), []);

    return (
        <SegmentRefreshContext.Provider value={refresh}>
            <SegmentContext.Provider value={segment}>{children}</SegmentContext.Provider>
        </SegmentRefreshContext.Provider>
    );
}

/** Current shopper segment from context. */
// eslint-disable-next-line react-refresh/only-export-components
export function useShopperSegment(): ShopperSegment {
    return useContext(SegmentContext);
}

/** Re-read the segment cookie after a same-page response that updated it (e.g. ATC rejected). */
// eslint-disable-next-line react-refresh/only-export-components
export function useSegmentRefresh(): () => void {
    return useContext(SegmentRefreshContext);
}

/** True when the current shopper segment allows ordering this item. */
// eslint-disable-next-line react-refresh/only-export-components
export function useIsAllowedBySegment(
    item?: ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'] | null
): boolean {
    return isAllowedBySegment(useShopperSegment(), item);
}
